import React, { useMemo } from 'react';
import { List, Tag, Typography, Button, Space, Spin, Alert } from 'antd';
import { ThunderboltOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { EmptyQueueState } from '@/components/sales/control-tower/EmptyQueueState';
import type { ControlTowerQueryParams } from '@/services/manufacturingControlTower.service';
import { useControlTowerBrief } from '@/hooks/useManufacturingControlTower';
import type { MfgLayoutProfile } from './layoutProfile';

const { Text } = Typography;

interface Action {
  severity: 'critical' | 'warning' | 'info';
  title: string;
  reason: string;
  actionLabel: string;
  deepLink: string;
  audience?: 'supervisor' | 'manager' | 'both';
}

interface Props {
  profile: MfgLayoutProfile;
  filters?: ControlTowerQueryParams;
  refreshInterval?: number;
}

const severityColor: Record<string, string> = {
  critical: 'red',
  warning: 'orange',
  info: 'blue',
};

export const AiBriefStrip: React.FC<Props> = ({ profile, filters, refreshInterval }) => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useControlTowerBrief(filters, refreshInterval);
  const brief = data as {
    asOf?: string;
    highlights?: string[];
    recommendedActions?: Action[];
  };

  const actions = useMemo(() => {
    const list = brief?.recommendedActions ?? [];
    return list.filter((a) => {
      const audience = a.audience ?? 'both';
      if (audience === 'both') return true;
      if (profile === 'supervisor') return audience === 'supervisor';
      if (profile === 'manager') return audience === 'manager';
      return true;
    });
  }, [brief?.recommendedActions, profile]);

  if (isLoading) {
    return (
      <ControlTowerSectionCard zone="attention" sectionId="ai-brief" title="AI Brief">
        <Spin />
      </ControlTowerSectionCard>
    );
  }

  if (isError) {
    return (
      <ControlTowerSectionCard zone="attention" sectionId="ai-brief" title="AI Brief">
        <Alert type="warning" showIcon message="Brief unavailable — panels below still refresh." />
      </ControlTowerSectionCard>
    );
  }

  return (
    <ControlTowerSectionCard
      zone="attention"
      sectionId="ai-brief"
      title={
        <Space>
          <ThunderboltOutlined />
          AI Brief + Recommended Actions
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      {(brief?.highlights?.length ?? 0) > 0 && (
        <Space wrap style={{ marginBottom: 12 }}>
          {brief!.highlights!.map((h) => (
            <Tag key={h} color="geekblue">
              {h}
            </Tag>
          ))}
        </Space>
      )}
      {!actions.length ? (
        <EmptyQueueState
          message="No recommended actions right now."
          actionLabel="Open execution board"
          onAction={() => navigate('/mfg/work-order-operations')}
        />
      ) : (
        <List
          size="small"
          dataSource={actions}
          renderItem={(a) => (
            <List.Item
              actions={[
                <Button
                  key="go"
                  type="link"
                  size="small"
                  icon={<RightOutlined />}
                  onClick={() => navigate(a.deepLink)}
                >
                  {a.actionLabel}
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <Tag color={severityColor[a.severity] || 'default'}>{a.severity}</Tag>
                    <Text strong>{a.title}</Text>
                  </Space>
                }
                description={
                  <Text type="secondary">
                    Why: {a.reason} · Do now: {a.actionLabel}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      )}
    </ControlTowerSectionCard>
  );
};

export default AiBriefStrip;
